
import { createRoute, useNavigate, Navigate, useSearch } from "@tanstack/react-router";
import { useSession } from "../auth-client";
import { Route as rootRoute } from "./__root";
import { Button } from "@/frontend/components/ui/button";
import { OnboardingLayout, LoadingScreen } from "@/frontend/components/onboarding/Onboarding";
import { useQuery } from "@tanstack/react-query";
import { orpc } from "@/frontend/orpc-client";
import { useOnboardingStateQuery } from "@/frontend/queries/sign";
import { useOnboardingSettingsQuery } from "@/frontend/queries/index/index";
import {
  ArrowRight,
  XCircle,
} from "lucide-react";
import ConsentSign from "../components/sign/ConsentSign";
import type { SignStatus } from "@/frontend/types/sign";


export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign",
  component: SignPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      continue: search.continue === "true" || search.continue === true,
    };
  },
});

function SignPage() {
  const { data: session, isPending } = useSession();
  const navigate = useNavigate();
  const { continue: showSigningForm } = useSearch({ from: "/sign" });
  const actorContextQuery = useQuery({
    ...orpc.org.getActorContext.queryOptions({ input: {} }),
    enabled: !!session,
  });

  const { data: settings, isLoading: isLoadingSettings } = useOnboardingSettingsQuery();

  // Fetch onboarding state - only when authenticated and feature is enabled
  const { data: onboardingState, isLoading: isLoadingState } = useOnboardingStateQuery(!!session && !!settings?.askConsentSignature);

  // Show loading while checking auth
  if (isPending) {
    return <LoadingScreen />;
  }

  // Require sign in - check BEFORE loading onboarding state
  if (!session) {
    return <Navigate to="/" />;
  }

  if (actorContextQuery.isLoading) {
    return <LoadingScreen />;
  }

  if (actorContextQuery.data?.actorType === "orgMember") {
    return <Navigate to="/dashboard/patients" search={{ patientMembershipId: undefined }} />;
  }
  
  if (isLoadingSettings) {
    return <LoadingScreen />;
  }

  if (!settings?.askConsentSignature) {
    return <Navigate to="/" />;
  }

  // Wait for onboarding state to load (only reached when authenticated)
  if (isLoadingState) {
    return <LoadingScreen />;
  }

  const signStatus = onboardingState?.signStatus as SignStatus | undefined;
  const firstName = session.user.name?.split(" ")[0] ?? "there";

  // If already signed, show completion state
  if (signStatus === "completed") {
        return <Navigate to="/" />;
  }

  // Show the actual signing form when ?continue=true — must come before rejected check
  if (showSigningForm) {
    return <ConsentSign />;
  }

  // If deleted, show re-sign screen
  if (signStatus === "deleted") {
    return (
      <OnboardingLayout>
        <div className="flex flex-col px-6 py-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-xl font-bold tracking-tight">Hi, {firstName}!</h1>
          </div>

          <div className="flex items-center gap-2.5 p-2.5 rounded-lg border bg-amber-50 border-amber-200 mb-6">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-amber-100">
              <XCircle className="w-4 h-4 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Signature not accepted</p>
              <p className="text-xs text-muted-foreground">Your previous signature was rejected</p>
            </div>
          </div>

          <p className="text-sm text-muted-foreground mb-8">
            Please sign again to continue accessing your health records.
          </p>

          <Button
            onClick={() => navigate({ to: "/sign", search: { continue: true } })}
            className="w-full"
            size="lg"
          >
            Sign authorization form
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </OnboardingLayout>
    );
  }

  // Sign intro page (default - pending state)
  return (
    <OnboardingLayout>
      <div className="flex flex-col px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-bold tracking-tight">Hi, {firstName}!</h1>
        </div>

        <p className="text-sm text-muted-foreground mb-5">
          To retrieve your medical records, we need your written authorization. This gives healthcare organizations permission to share your records with us securely.
        </p>

        <p className="text-sm font-semibold mb-3">How it works</p>
        <div className="space-y-2 mb-8">
          <div className="flex items-start gap-3 p-3 rounded-lg border bg-muted/50 border-transparent">
            <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-[10px] font-bold text-primary-foreground">1</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Prepare your materials</p>
              <p className="text-xs text-muted-foreground mt-0.5">Grab a blank piece of white paper and a pen.</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 rounded-lg border bg-muted/50 border-transparent">
            <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-[10px] font-bold text-primary-foreground">2</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Write your signature</p>
              <p className="text-xs text-muted-foreground mt-0.5">Sign your name clearly in the center of the paper.</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 rounded-lg border bg-muted/50 border-transparent">
            <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-[10px] font-bold text-primary-foreground">3</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Capture it</p>
              <p className="text-xs text-muted-foreground mt-0.5">Use your camera to photograph the signature within the guide frame.</p>
            </div>
          </div>
        </div>

        <Button
          onClick={() => navigate({ to: "/sign", search: { continue: true } })}
          className="w-full"
          size="lg"
        >
          Continue to sign
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </OnboardingLayout>
  );
}

